<template>
  <b-card>
    <h4 class="card-title d-flex align-items-center">
      eKiosk KPI Report
      <kpi-process-flow-help class="ml-50" />
    </h4>
    <b-row class="mb-3">
      <b-col md="3">
        <label class="d-block">From</label>
        <b-form-input v-model="fromDate" type="date" />
      </b-col>
      <b-col md="3">
        <label class="d-block">To</label>
        <b-form-input v-model="toDate" type="date" />
      </b-col>
      <b-col md="5">
        <label class="d-block">Counter</label>
        <b-form-select v-model="selectedCounterName" :options="counterOptions" />
      </b-col>
      <b-col md="1" class="d-flex align-items-end">
        <b-button variant="primary" :disabled="loading || !!validationError" @click="load">
          Load
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="12" class="d-flex align-items-end">
      <b-button variant="success" :disabled="loading || rows.length <= 0">
        <download-excel
          :data="rowsForDownload"
          :fields="downloadFields"
          :name="downloadFileName"
          class="download-border"
        >
          Download Excel
        </download-excel>
      </b-button>
    </b-col>
    </b-row>

    <b-alert v-if="validationError" show variant="warning" class="mb-2">{{ validationError }}</b-alert>
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <b-table
      show-empty
      :items="paginatedRows"
      :fields="fields"
      responsive
      class="mt-2 kpi-report-table"
      thead-class="compact-header"
      :key="kpiTableKey"
    >
      <template #cell(counterName)="row">
        <span>
          {{ row.item.counterName }}
          <b-badge v-if="row.item.isMissedQueue" variant="warning" class="ml-1">Missed Queue</b-badge>
        </span>
      </template>
      <template #cell(queueTimeMin)="row">
        <span :class="kpiStatusTextClass(row.value, 'QUEUE_TIME')" class="font-weight-bold">
          {{ row.value }}
        </span>
      </template>
      <template #cell(poCreationDurationMin)="row">
        <span :class="kpiStatusTextClass(row.value, 'PO_CREATION_DURATION')" class="font-weight-bold">
          {{ row.value }}
        </span>
      </template>
      <template #cell(logisticResponseTimeMin)="row">
        <span :class="kpiStatusTextClass(row.value, 'LOGISTIC_RESPONSE_TIME')" class="font-weight-bold">
          {{ row.value }}
        </span>
      </template>
      <template #cell(counterDurationMin)="row">
        <span :class="kpiStatusTextClass(row.value, 'COUNTER_DURATION')" class="font-weight-bold">
          {{ row.value }}
        </span>
      </template>
      <template #cell(customerExperienceTimeMin)="row">
        <span :class="kpiStatusTextClass(row.value, 'CUSTOMER_EXPERIENCE_TIME')" class="font-weight-bold">
          {{ row.value }}
        </span>
      </template>
    </b-table>
    <b-row v-if="rows.length > pageSize" class="mt-2">
      <b-col cols="12" class="d-flex justify-content-end">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows.length"
          :per-page="pageSize"
          first-number
          last-number
          align="right"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import KpiProcessFlowHelp from './kpi-process-flow-help.vue';

const SINGAPORE_TIMEZONE = 'Asia/Singapore';

const KPI_COLUMN_TO_KPI = {
  queueTimeMin: 'QUEUE_TIME',
  poCreationDurationMin: 'PO_CREATION_DURATION',
  logisticResponseTimeMin: 'LOGISTIC_RESPONSE_TIME',
  counterDurationMin: 'COUNTER_DURATION',
  customerExperienceTimeMin: 'CUSTOMER_EXPERIENCE_TIME',
};

const BASE_FIELDS = [
  { key: 'counterName', label: 'Counter Name' },
  { key: 'reportDate', label: 'Date' },
  { key: 'queueNumber', label: 'Queue number' },
  { key: 'poNumber', label: 'PO Number' },
  { key: 'poPrefix', label: 'PO prefix' },
  { key: 'soNumber', label: 'SO Number' },
  { key: 'queueCreated', label: 'Queue created' },
  { key: 'queueServed', label: 'Queue serve' },
  { key: 'sentToLogistic', label: 'Sent to Logistic' },
  { key: 'checkStockTime', label: 'Check Stock Time' },
  { key: 'stockCheckCompleted', label: 'Stock Check Completed' },
  { key: 'submitPoToSap', label: 'Submit PO to SAP' },
  { key: 'scanCompleted', label: 'Scan Completed' },
  { key: 'queueTimeMin', label: 'Queue Time (min)' },
  { key: 'poCreationDurationMin', label: 'PO creation duration (min)' },
  { key: 'logisticResponseTimeMin', label: 'Logistic Response Time (min)' },
  { key: 'counterDurationMin', label: 'Counter Duration (min)' },
  { key: 'customerExperienceTimeMin', label: 'Customer Experience Time (min)' },
];

function defaultDates() {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 86400000);
  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
}

export default {
  components: {
    KpiProcessFlowHelp,
  },
  data() {
    const d = defaultDates();
    return {
      fromDate: d.fromDate,
      toDate: d.toDate,
      selectedCounterName: 'all',
      availableCounters: [],
      rows: [],
      currentPage: 1,
      pageSize: 10,
      loading: false,
      error: '',
      kpiThresholds: {},
    };
  },
  mounted() {
    document.title = 'eKiosk KPI Report | RSP';
    this.load();
  },
  methods: {
    diffDaysInclusive(fromDateStr, toDateStr) {
      const from = new Date(fromDateStr);
      const to = new Date(toDateStr);
      const ms = to.getTime() - from.getTime();
      if (!Number.isFinite(ms)) return null;
      return Math.floor(ms / 86400000) + 1;
    },
    fmtTime(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '-';
      // Show both date and time
      return d.toLocaleString('en-GB', {
        timeZone: SINGAPORE_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    fmtDate(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('en-GB', {
        timeZone: SINGAPORE_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    fmtMin(v) {
      if (v == null || v === '') return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    buildKpiThresholdMap(items) {
      const map = {};
      for (const item of items) {
        if (!item || !item.kpiName) continue;
        map[item.kpiName] = Number(item.thresholdMinutes);
      }
      return map;
    },
    kpiDisplayLabel(kpiName, fallback) {
      const items = this.$store.state.kioskKpi.masterItems || [];
      const found = items.find((item) => item && item.kpiName === kpiName);
      const displayName = found && found.displayName;
      return displayName ? `${displayName} (min)` : fallback;
    },
    kpiStatusTextClass(value, kpiName) {
      const threshold = this.kpiThresholds[kpiName];
      if (threshold == null || !Number.isFinite(threshold)) return '';
      const minutes = Number(value);
      if (!Number.isFinite(minutes)) return '';
      return minutes > Number(threshold) ? 'text-danger' : 'text-success';
    },
    firstLogisticFlow(row) {
      const flows = row && Array.isArray(row.logisticFlows) ? row.logisticFlows : [];
      return flows.length > 0 ? flows[0] : null;
    },
    normalizeRow(row) {
      const isMissedQueue = !!row.isMissedQueue;
      const ts = row && row.timestamps ? row.timestamps : {};
      const lf = this.firstLogisticFlow(row) || {};
      const computed = row && row.computed ? row.computed : {};

      const poPrefix =
        row.poPrefix ||
        row.po_prefix ||
        (row.poNumber && String(row.poNumber).includes('-')
          ? String(row.poNumber).split('-')[0]
          : '') ||
        '';

      return {
        // raw-ish identifiers
        counterId: row.counterId || null,
        counterName: row.counterName || '-',
        reportDate: this.fmtDate(ts.queueCreatedAt),
        queueNumber: row.queueNumber || '-',
        poNumber: row.poNumber || '-',
        poPrefix: poPrefix || '-',
        soNumber: row.soNumber || '-',
        isMissedQueue,

        // times (HH:mm:ss, SG)
        queueCreated: this.fmtTime(ts.queueCreatedAt),
        queueServed: this.fmtTime(ts.queueServedAt),
        sentToLogistic: this.fmtTime(lf.sentToLogisticAt),
        checkStockTime: this.fmtTime(lf.checkStockStartAt),
        stockCheckCompleted: this.fmtTime(lf.checkStockCompletedAt),
        submitPoToSap: this.fmtTime(ts.submitToSAPAt),
        scanCompleted: this.fmtTime(ts.scanCompletedAt),

        // durations (min)
        queueTimeMin: this.fmtMin(computed.queueTime),
        poCreationDurationMin: this.fmtMin(computed.poCreationDuration),
        logisticResponseTimeMin: this.fmtMin(computed.logisticResponseTime),
        counterDurationMin: this.fmtMin(computed.counterDuration),
        customerExperienceTimeMin: this.fmtMin(computed.customerExperienceTime),

        missedQueueLabel: isMissedQueue ? 'Yes' : 'No',
      };
    },
    async load() {
      this.error = '';
      this.loading = true;
      this.currentPage = 1;
      const fromIso = new Date(this.fromDate).toISOString();
      const toEnd = new Date(this.toDate);
      toEnd.setHours(23, 59, 59, 999);
      const params = {
        from: fromIso,
        to: toEnd.toISOString(),
      };
      if (this.selectedCounterName && this.selectedCounterName !== 'all') {
        params.counterName = this.selectedCounterName;
      }
      try {
        const [reportResult, masterResult] = await Promise.allSettled([
          this.$store.dispatch('kioskKpi/fetchKpiReport', params),
          this.$store.dispatch('kioskKpi/fetchKpiMaster'),
        ]);

        if (reportResult.status === 'rejected') {
          throw reportResult.reason;
        }

        const masterItems =
          masterResult.status === 'fulfilled'
            ? masterResult.value.data || []
            : this.$store.getters['kioskKpi/masterItems'] || [];
        this.kpiThresholds = this.buildKpiThresholdMap(masterItems);

        const result = reportResult.value;
        const rawRows = result.data || [];
        const normalized = rawRows.map((r) => this.normalizeRow(r));

        const metaCounters =
          result.meta && Array.isArray(result.meta.counters)
            ? result.meta.counters
            : [];
        this.availableCounters = metaCounters
          .map((name) => String(name || '').trim())
          .filter((name) => name.length > 0)
          .sort((a, b) => a.localeCompare(b));

        this.rows =
          this.selectedCounterName && this.selectedCounterName !== 'all'
            ? normalized.filter(
                (r) => String(r.counterName) === String(this.selectedCounterName)
              )
            : normalized;
      } catch (err) {
        this.rows = [];
        this.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Failed to load report';
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    fields() {
      void this.kpiThresholds;
      void this.$store.state.kioskKpi.masterItems;
      return BASE_FIELDS.map((field) => {
        const kpiName = KPI_COLUMN_TO_KPI[field.key];
        if (!kpiName) return field;
        return { ...field, label: this.kpiDisplayLabel(kpiName, field.label) };
      });
    },
    downloadFields() {
      void this.$store.state.kioskKpi.masterItems;
      const fields = {
        'Counter Name': 'counterName',
        Date: 'reportDate',
        'Queue number': 'queueNumber',
        'PO Number': 'poNumber',
        'PO prefix': 'poPrefix',
        'SO Number': 'soNumber',
        'Queue created': 'queueCreated',
        'Queue serve': 'queueServed',
        'Sent to Logistic': 'sentToLogistic',
        'Check Stock Time': 'checkStockTime',
        'Stock Check Completed': 'stockCheckCompleted',
        'Submit PO to SAP': 'submitPoToSap',
        'Scan Completed': 'scanCompleted',
      };
      for (const [key, kpiName] of Object.entries(KPI_COLUMN_TO_KPI)) {
        const base = BASE_FIELDS.find((field) => field.key === key);
        const label = this.kpiDisplayLabel(kpiName, base ? base.label : key);
        fields[label] = key;
      }
      fields['Missed Queue'] = 'missedQueueLabel';
      return fields;
    },
    kpiTableKey() {
      const thresholds = Object.entries(this.kpiThresholds)
        .map(([name, value]) => `${name}:${value}`)
        .join('|');
      const labels = (this.$store.state.kioskKpi.masterItems || [])
        .map((item) => `${item.kpiName}:${item.displayName}`)
        .join('|');
      return `${thresholds}||${labels}`;
    },
    validationError() {
      const days = this.diffDaysInclusive(this.fromDate, this.toDate);
      if (days == null) return '';
      if (days <= 0) return 'Invalid date range.';
      if (days > 31) return 'Max date range is 1 month (31 days).';
      return '';
    },
    counterOptions() {
      const opts = [{ value: 'all', text: 'All counter' }];
      for (const name of this.availableCounters) {
        opts.push({
          value: name,
          text: name,
        });
      }
      return opts;
    },
    rowsForDownload() {
      return this.rows.map((row) => ({ ...row }));
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
    downloadFileName() {
      return `eKiosk-KPI-Report-${Date.now()}.xls`;
    },
  },
};
</script>
